import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../firebase';

const IMPORTANT_FIELDS = [
  { name: 'nom', label: 'Nom', type: 'string' },
  { name: 'prenom', label: 'Prénom', type: 'string' },
  { name: 'phone', label: 'Téléphone', type: 'string' },
  { name: 'elo', label: 'ELO', type: 'number' },
  { name: 'matchCount', label: 'Nombre de matchs', type: 'number' },
  { name: 'winTournement', label: 'Tournois gagnés', type: 'number' },
];

const FIELD_TYPES = IMPORTANT_FIELDS.reduce((acc, field) => {
  acc[field.name] = field.type;
  return acc;
}, {});

const FIELD_LABELS = IMPORTANT_FIELDS.reduce((acc, field) => {
  acc[field.name] = field.label;
  return acc;
}, {});

const AdminJoueurs = () => {
  const [joueurs, setJoueurs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedJoueur, setSelectedJoueur] = useState(null);
  const [editData, setEditData] = useState({});
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldValue, setNewFieldValue] = useState('');
  const [newFieldType, setNewFieldType] = useState('string');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedId, setCopiedId] = useState('');

  const normalizeText = (value) =>
    value
      ?.toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || '';

  const getCategorieFromElo = (elo) => {
    const value = Number(elo || 0);

    if (value >= 2000) return 'P500';
    if (value >= 1600) return 'P400';
    if (value >= 1200) return 'P300';
    if (value >= 900) return 'P200';
    if (value >= 800) return 'P150';
    if (value >= 700) return 'P100';
    if (value >= 200) return 'P50';

    return 'Non classé';
  };

  const hasField = (object, fieldName) =>
    Object.prototype.hasOwnProperty.call(object, fieldName);

  const isFirestoreTimestamp = (value) =>
    value &&
    typeof value === 'object' &&
    typeof value.toDate === 'function';

  const sanitizeForExport = (value) => {
    if (isFirestoreTimestamp(value)) {
      return {
        _type: 'timestamp',
        iso: value.toDate().toISOString(),
        seconds: value.seconds,
        nanoseconds: value.nanoseconds,
      };
    }

    if (Array.isArray(value)) {
      return value.map((item) => sanitizeForExport(item));
    }

    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [
          key,
          sanitizeForExport(val),
        ])
      );
    }

    return value;
  };

  const valueToSearchText = (value) => {
    if (value === null || value === undefined) return '';

    if (isFirestoreTimestamp(value)) {
      return value.toDate().toISOString();
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(sanitizeForExport(value));
      } catch {
        return '';
      }
    }

    return String(value);
  };

  const getFieldType = (fieldName, value) => {
    if (FIELD_TYPES[fieldName]) return FIELD_TYPES[fieldName];

    if (isFirestoreTimestamp(value)) return 'timestamp';
    if (Array.isArray(value)) return 'json';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';

    if (value && typeof value === 'object') return 'json';

    return 'string';
  };

  const formatValueForInput = (value) => {
    if (value === null || value === undefined) return '';

    if (isFirestoreTimestamp(value)) {
      return value.toDate().toISOString();
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(sanitizeForExport(value), null, 2);
      } catch {
        return String(value);
      }
    }

    return String(value);
  };

  const parseEditedValue = (fieldName, originalValue, editedValue) => {
    const fieldType = getFieldType(fieldName, originalValue);

    if (fieldType === 'timestamp') {
      return originalValue;
    }

    if (fieldType === 'number') {
      if (editedValue === '') return 0;

      const numberValue = Number(editedValue);

      if (Number.isNaN(numberValue)) {
        throw new Error(`Le champ "${fieldName}" doit être un nombre.`);
      }

      return numberValue;
    }

    if (fieldType === 'boolean') {
      return editedValue === true || editedValue === 'true';
    }

    if (fieldType === 'json') {
      if (editedValue === '') return null;

      try {
        return JSON.parse(editedValue);
      } catch {
        throw new Error(`Le champ "${fieldName}" contient un JSON invalide.`);
      }
    }

    return editedValue;
  };

  const parseNewFieldValue = () => {
    if (newFieldType === 'number') {
      if (newFieldValue === '') return 0;

      const numberValue = Number(newFieldValue);

      if (Number.isNaN(numberValue)) {
        alert('La valeur doit être un nombre.');
        return undefined;
      }

      return numberValue;
    }

    if (newFieldType === 'boolean') {
      return newFieldValue === 'true';
    }

    if (newFieldType === 'json') {
      if (newFieldValue === '') return null;

      try {
        return JSON.parse(newFieldValue);
      } catch {
        alert('JSON invalide.');
        return undefined;
      }
    }

    return newFieldValue;
  };

  const valuesAreEqual = (oldValue, newValue) => {
    return (
      JSON.stringify(sanitizeForExport(oldValue)) ===
      JSON.stringify(sanitizeForExport(newValue))
    );
  };

  const getVisibleFields = (joueur) => {
    if (!joueur) return [];

    const importantFieldNames = IMPORTANT_FIELDS.map((field) => field.name);

    const existingFieldNames = Object.keys(joueur).filter(
      (key) => key !== 'id'
    );

    const otherFields = existingFieldNames
      .filter((key) => !importantFieldNames.includes(key))
      .sort((a, b) => a.localeCompare(b));

    return [...importantFieldNames, ...otherFields];
  };

  const fetchJoueurs = async () => {
    try {
      setIsLoading(true);

      const snap = await getDocs(collection(db, 'joueurs'));

      const list = snap.docs.map((document) => ({
        id: document.id,
        ...document.data(),
      }));

      list.sort((a, b) => {
        const nameA = normalizeText(`${a.nom || ''} ${a.prenom || ''}`);
        const nameB = normalizeText(`${b.nom || ''} ${b.prenom || ''}`);

        if (nameA && nameB) return nameA.localeCompare(nameB);
        if (nameA) return -1;
        if (nameB) return 1;

        return a.id.localeCompare(b.id);
      });

      setJoueurs(list);
    } catch (error) {
      console.error('Erreur chargement joueurs :', error);
      alert('Erreur pendant le chargement des joueurs.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJoueurs();
  }, []);

  const filteredJoueurs = useMemo(() => {
    const term = normalizeText(searchTerm);

    if (!term) return joueurs;

    return joueurs.filter((joueur) => {
      const searchableValues = [
        joueur.id,
        joueur.nom,
        joueur.prenom,
        joueur.phone,
        joueur.elo,
        joueur.matchCount,
        joueur.winTournement,
        getCategorieFromElo(joueur.elo),
        `${joueur.nom || ''} ${joueur.prenom || ''}`,
        `${joueur.prenom || ''} ${joueur.nom || ''}`,
        ...Object.values(joueur).map((value) => valueToSearchText(value)),
      ];

      return searchableValues.some((value) =>
        normalizeText(value).includes(term)
      );
    });
  }, [searchTerm, joueurs]);

  const openJoueur = (joueur) => {
    setSelectedJoueur(joueur);

    const preparedData = {};

    getVisibleFields(joueur).forEach((fieldName) => {
      preparedData[fieldName] = formatValueForInput(joueur[fieldName]);
    });

    setEditData(preparedData);
    setNewFieldName('');
    setNewFieldValue('');
    setNewFieldType('string');
  };

  const closeJoueur = () => {
    setSelectedJoueur(null);
    setEditData({});
    setNewFieldName('');
    setNewFieldValue('');
    setNewFieldType('string');
  };

  const handleFieldChange = (fieldName, value) => {
    setEditData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const saveJoueur = async () => {
    if (!selectedJoueur) return;

    try {
      setIsSaving(true);

      const updates = {};
      const visibleFields = getVisibleFields(selectedJoueur);

      visibleFields.forEach((fieldName) => {
        const originalValue = selectedJoueur[fieldName];
        const editedValue = editData[fieldName] ?? '';

        if (!hasField(selectedJoueur, fieldName) && editedValue === '') {
          return;
        }

        const parsedValue = parseEditedValue(
          fieldName,
          originalValue,
          editedValue
        );

        if (!valuesAreEqual(originalValue, parsedValue)) {
          updates[fieldName] = parsedValue;
        }
      });

      if (Object.keys(updates).length === 0) {
        alert('Aucune modification à enregistrer.');
        return;
      }

      await updateDoc(doc(db, 'joueurs', selectedJoueur.id), updates);

      const updatedSelectedJoueur = {
        ...selectedJoueur,
        ...updates,
      };

      setSelectedJoueur(updatedSelectedJoueur);

      setJoueurs((prev) =>
        prev.map((joueur) =>
          joueur.id === selectedJoueur.id
            ? {
                ...joueur,
                ...updates,
              }
            : joueur
        )
      );

      alert('Joueur modifié avec succès.');
    } catch (error) {
      console.error('Erreur sauvegarde joueur :', error);
      alert(error.message || 'Erreur pendant la sauvegarde.');
    } finally {
      setIsSaving(false);
    }
  };

  const addField = async () => {
    if (!selectedJoueur) return;

    const fieldName = newFieldName.trim();

    if (!fieldName) {
      alert('Indique un nom de champ.');
      return;
    }

    if (fieldName === 'id') {
      alert('Tu ne peux pas modifier le champ id.');
      return;
    }

    if (hasField(selectedJoueur, fieldName)) {
      alert(
        `Le champ "${fieldName}" existe déjà. Modifie-le directement dans la fiche.`
      );
      return;
    }

    const parsedValue = parseNewFieldValue();

    if (parsedValue === undefined) return;

    try {
      setIsSaving(true);

      await updateDoc(doc(db, 'joueurs', selectedJoueur.id), {
        [fieldName]: parsedValue,
      });

      const updatedJoueur = {
        ...selectedJoueur,
        [fieldName]: parsedValue,
      };

      setSelectedJoueur(updatedJoueur);

      setEditData((prev) => ({
        ...prev,
        [fieldName]: formatValueForInput(parsedValue),
      }));

      setJoueurs((prev) =>
        prev.map((joueur) =>
          joueur.id === selectedJoueur.id
            ? {
                ...joueur,
                [fieldName]: parsedValue,
              }
            : joueur
        )
      );

      setNewFieldName('');
      setNewFieldValue('');
      setNewFieldType('string');

      alert('Champ ajouté.');
    } catch (error) {
      console.error('Erreur ajout champ :', error);
      alert("Erreur pendant l'ajout du champ.");
    } finally {
      setIsSaving(false);
    }
  };

  const removeField = async (fieldName) => {
    if (!selectedJoueur) return;

    if (fieldName === 'id') {
      alert('Impossible de supprimer l’ID.');
      return;
    }

    if (!hasField(selectedJoueur, fieldName)) {
      alert("Ce champ n'existe pas encore dans ce document.");
      return;
    }

    const confirmation = window.confirm(
      `Tu veux vraiment supprimer le champ "${fieldName}" de ce joueur ?`
    );

    if (!confirmation) return;

    try {
      setIsSaving(true);

      await updateDoc(doc(db, 'joueurs', selectedJoueur.id), {
        [fieldName]: deleteField(),
      });

      const updatedJoueur = { ...selectedJoueur };
      delete updatedJoueur[fieldName];

      const updatedEditData = { ...editData };
      delete updatedEditData[fieldName];

      setSelectedJoueur(updatedJoueur);
      setEditData(updatedEditData);

      setJoueurs((prev) =>
        prev.map((joueur) => {
          if (joueur.id !== selectedJoueur.id) return joueur;

          const updated = { ...joueur };
          delete updated[fieldName];

          return updated;
        })
      );

      alert('Champ supprimé.');
    } catch (error) {
      console.error('Erreur suppression champ :', error);
      alert('Erreur pendant la suppression du champ.');
    } finally {
      setIsSaving(false);
    }
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedId(value);

      setTimeout(() => {
        setCopiedId('');
      }, 1500);
    } catch {
      alert('Impossible de copier.');
    }
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const today = new Date().toISOString().slice(0, 10);

    const backup = {
      collection: 'joueurs',
      exportedAt: new Date().toISOString(),
      total: joueurs.length,
      data: joueurs.map((joueur) => sanitizeForExport(joueur)),
    };

    const content = JSON.stringify(backup, null, 2);

    downloadFile(
      content,
      `backup_complet_joueurs_mendyx_${today}.json`,
      'application/json'
    );
  };

  const escapeCSV = (value) => {
    const stringValue =
      value === null || value === undefined ? '' : String(value);

    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  const exportCSV = () => {
    const today = new Date().toISOString().slice(0, 10);

    const importantKeys = IMPORTANT_FIELDS.map((field) => field.name);

    const allKeysSet = new Set();

    joueurs.forEach((joueur) => {
      Object.keys(joueur).forEach((key) => allKeysSet.add(key));
    });

    const otherKeys = Array.from(allKeysSet)
      .filter((key) => key !== 'id' && !importantKeys.includes(key))
      .sort((a, b) => a.localeCompare(b));

    const headers = ['id', ...importantKeys, ...otherKeys];

    const rows = joueurs.map((joueur) =>
      headers.map((header) => {
        const value = joueur[header];

        if (value && typeof value === 'object') {
          return JSON.stringify(sanitizeForExport(value));
        }

        return value ?? '';
      })
    );

    const csvContent = [
      headers.map(escapeCSV).join(';'),
      ...rows.map((row) => row.map(escapeCSV).join(';')),
    ].join('\n');

    downloadFile(
      csvContent,
      `export_complet_joueurs_mendyx_${today}.csv`,
      'text/csv;charset=utf-8'
    );
  };

  const visibleFields = selectedJoueur ? getVisibleFields(selectedJoueur) : [];

  return (
    <section className="min-h-screen bg-gradient-to-b from-white to-[#f7f4ff] text-black">
      <div className="w-full max-w-[1600px] mx-auto pt-16 md:pt-24 pb-10 px-3 sm:px-6 lg:px-12">
        <div className="text-center mb-5 md:mb-10">
          <p className="text-[10px] md:text-xs font-bold uppercase tracking-[0.25em] text-[#4c2f8e] mb-2">
            Admin Mendyx
          </p>

          <h1 className="text-3xl md:text-6xl font-black text-[#4c2f8e] leading-tight">
            Gestion des joueurs
          </h1>

          <p className="text-gray-500 mt-2 text-sm md:text-lg">
            Recherche, modification, suppression de champs et sauvegarde complète
            de la collection joueurs.
          </p>
        </div>

        <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl border border-purple-100 p-3 md:p-5 mb-4 md:mb-8">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 text-gray-400">
                🔍
              </span>

              <input
                type="text"
                placeholder="Rechercher nom, prénom, téléphone, ID, ELO, winTournement..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-200 rounded-xl md:rounded-2xl pl-10 md:pl-14 pr-4 py-3 md:py-4 text-sm md:text-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
              />
            </div>

            <button
              type="button"
              onClick={fetchJoueurs}
              className="bg-white border border-purple-200 text-[#4c2f8e] px-5 py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-bold hover:border-[#4c2f8e] transition"
            >
              🔄 Recharger
            </button>

            <button
              type="button"
              onClick={exportJSON}
              className="bg-[#4c2f8e] text-white px-5 py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-bold hover:bg-[#3d2575] transition"
            >
              ⬇ Backup JSON
            </button>

            <button
              type="button"
              onClick={exportCSV}
              className="bg-white border border-purple-200 text-[#4c2f8e] px-5 py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-bold hover:border-[#4c2f8e] transition"
            >
              ⬇ Export CSV
            </button>
          </div>

          <div className="mt-3 text-xs md:text-sm text-gray-500 flex flex-col md:flex-row md:items-center md:justify-between gap-1">
            <span>
              Résultat :{' '}
              <strong className="text-[#4c2f8e]">
                {filteredJoueurs.length}
              </strong>{' '}
              /{' '}
              <strong className="text-[#4c2f8e]">{joueurs.length}</strong>{' '}
              joueurs
            </span>

            <span>
              Le backup JSON exporte les {joueurs.length} documents complets de
              la collection joueurs.
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-xl border border-purple-100 p-3 md:p-5">
            {[...Array(10)].map((_, index) => (
              <div
                key={index}
                className="h-14 bg-gray-100 rounded-xl mb-2 animate-pulse"
              />
            ))}
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-2">
              {filteredJoueurs.length > 0 ? (
                filteredJoueurs.map((joueur) => (
                  <div
                    key={joueur.id}
                    className="bg-white rounded-xl border border-purple-100 shadow-sm p-3"
                  >
                    <div className="font-black text-gray-900">
                      {joueur.nom || 'Sans nom'} {joueur.prenom || ''}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      📞 {joueur.phone || 'Pas de téléphone'}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      Elo :{' '}
                      <strong className="text-[#4c2f8e]">
                        {joueur.elo ?? 0}
                      </strong>{' '}
                      · {getCategorieFromElo(joueur.elo)}
                    </div>

                    <div className="text-xs text-gray-500 mt-1">
                      Matchs :{' '}
                      <strong>{joueur.matchCount ?? 0}</strong> · Tournois
                      gagnés :{' '}
                      <strong className="text-[#4c2f8e]">
                        {joueur.winTournement ?? 0}
                      </strong>
                    </div>

                    <div className="mt-2 bg-gray-50 rounded-lg p-2 text-[11px] text-gray-600 break-all">
                      {joueur.id}
                    </div>

                    <div className="flex gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => openJoueur(joueur)}
                        className="flex-1 bg-[#4c2f8e] text-white px-3 py-2 rounded-lg text-xs font-bold"
                      >
                        Modifier
                      </button>

                      <button
                        type="button"
                        onClick={() => copyToClipboard(joueur.id)}
                        className="flex-1 bg-white border border-purple-200 text-[#4c2f8e] px-3 py-2 rounded-lg text-xs font-bold"
                      >
                        {copiedId === joueur.id ? 'Copié ✅' : 'Copier ID'}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-6">
                  Aucun joueur trouvé.
                </div>
              )}
            </div>

            <div className="hidden md:block overflow-hidden bg-white border border-purple-100 shadow-2xl rounded-3xl">
              <table className="w-full text-left">
                <thead className="bg-[#4c2f8e] text-white">
                  <tr>
                    <th className="px-5 py-4">Joueur</th>
                    <th className="px-5 py-4">Téléphone</th>
                    <th className="px-5 py-4">ELO</th>
                    <th className="px-5 py-4">Catégorie</th>
                    <th className="px-5 py-4">Matchs</th>
                    <th className="px-5 py-4">winTournement</th>
                    <th className="px-5 py-4">ID Firestore</th>
                    <th className="px-5 py-4 text-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredJoueurs.length > 0 ? (
                    filteredJoueurs.map((joueur) => (
                      <tr
                        key={joueur.id}
                        className="border-b hover:bg-[#f7f4ff]"
                      >
                        <td className="px-5 py-4 font-bold">
                          {joueur.nom || 'Sans nom'} {joueur.prenom || ''}
                        </td>

                        <td className="px-5 py-4">
                          {joueur.phone || (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        <td className="px-5 py-4 font-black text-[#4c2f8e]">
                          {joueur.elo ?? 0}
                        </td>

                        <td className="px-5 py-4">
                          {getCategorieFromElo(joueur.elo)}
                        </td>

                        <td className="px-5 py-4">
                          {joueur.matchCount ?? 0}
                        </td>

                        <td className="px-5 py-4 font-black text-[#4c2f8e]">
                          {joueur.winTournement ?? 0}
                        </td>

                        <td className="px-5 py-4">
                          <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">
                            {joueur.id}
                          </code>
                        </td>

                        <td className="px-5 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openJoueur(joueur)}
                              className="bg-[#4c2f8e] text-white px-4 py-2 rounded-full text-sm font-bold hover:bg-[#3d2575] transition"
                            >
                              Modifier
                            </button>

                            <button
                              type="button"
                              onClick={() => copyToClipboard(joueur.id)}
                              className="bg-white border border-purple-200 text-[#4c2f8e] px-4 py-2 rounded-full text-sm font-bold hover:border-[#4c2f8e] transition"
                            >
                              {copiedId === joueur.id ? 'Copié ✅' : 'ID'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-8 py-8 text-center text-gray-500"
                      >
                        Aucun joueur trouvé.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {selectedJoueur && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start md:items-center justify-center p-3 md:p-8 overflow-y-auto">
            <div className="bg-white w-full max-w-6xl rounded-2xl md:rounded-3xl shadow-2xl border border-purple-100 overflow-hidden">
              <div className="bg-[#4c2f8e] text-white px-4 md:px-8 py-4 md:py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-xl md:text-3xl font-black">
                    Modifier le joueur
                  </h2>

                  <p className="text-xs md:text-sm text-purple-100 break-all mt-1">
                    ID : {selectedJoueur.id}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={closeJoueur}
                  className="bg-white text-[#4c2f8e] px-4 py-2 rounded-full text-sm font-bold"
                >
                  Fermer
                </button>
              </div>

              <div className="p-4 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleFields.map((fieldName) => {
                    const originalValue = selectedJoueur[fieldName];
                    const existsInDocument = hasField(
                      selectedJoueur,
                      fieldName
                    );
                    const fieldType = getFieldType(fieldName, originalValue);
                    const isTimestamp = fieldType === 'timestamp';
                    const isJson = fieldType === 'json';
                    const isBoolean = fieldType === 'boolean';
                    const isNumber = fieldType === 'number';

                    return (
                      <div
                        key={fieldName}
                        className={`border rounded-2xl p-3 md:p-4 ${
                          fieldName === 'winTournement'
                            ? 'border-[#4c2f8e] bg-[#f7f4ff]'
                            : 'border-purple-100 bg-[#fbfaff]'
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div>
                            <label className="text-sm font-black text-[#4c2f8e]">
                              {FIELD_LABELS[fieldName] || fieldName}
                            </label>

                            {FIELD_LABELS[fieldName] && (
                              <div className="text-[11px] text-gray-400">
                                Champ Firebase : {fieldName}
                              </div>
                            )}

                            {!existsInDocument && (
                              <div className="text-[11px] text-orange-500 font-bold">
                                Ce champ n’existe pas encore sur ce joueur.
                              </div>
                            )}
                          </div>

                          {existsInDocument && (
                            <button
                              type="button"
                              onClick={() => removeField(fieldName)}
                              className="text-xs font-bold text-red-600 hover:underline"
                            >
                              Supprimer champ
                            </button>
                          )}
                        </div>

                        {isTimestamp ? (
                          <input
                            type="text"
                            value={editData[fieldName] || ''}
                            disabled
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-gray-100 text-gray-500"
                          />
                        ) : isBoolean ? (
                          <select
                            value={editData[fieldName] || 'false'}
                            onChange={(e) =>
                              handleFieldChange(fieldName, e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : isJson ? (
                          <textarea
                            value={editData[fieldName] || ''}
                            onChange={(e) =>
                              handleFieldChange(fieldName, e.target.value)
                            }
                            rows={6}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                          />
                        ) : (
                          <input
                            type={isNumber ? 'number' : 'text'}
                            value={editData[fieldName] || ''}
                            onChange={(e) =>
                              handleFieldChange(fieldName, e.target.value)
                            }
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                          />
                        )}

                        <p className="text-[11px] text-gray-400 mt-2">
                          Type utilisé : {fieldType}
                        </p>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 border-2 border-dashed border-purple-200 rounded-2xl p-4 bg-white">
                  <h3 className="text-lg font-black text-[#4c2f8e] mb-3">
                    Ajouter un champ
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      placeholder="Nom du champ"
                      value={newFieldName}
                      onChange={(e) => setNewFieldName(e.target.value)}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                    />

                    <select
                      value={newFieldType}
                      onChange={(e) => {
                        setNewFieldType(e.target.value);
                        setNewFieldValue('');
                      }}
                      className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                    >
                      <option value="string">Texte</option>
                      <option value="number">Nombre</option>
                      <option value="boolean">Booléen</option>
                      <option value="json">JSON / Objet / Tableau</option>
                    </select>

                    {newFieldType === 'boolean' ? (
                      <select
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                      >
                        <option value="">Choisir...</option>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : (
                      <input
                        type={newFieldType === 'number' ? 'number' : 'text'}
                        placeholder="Valeur"
                        value={newFieldValue}
                        onChange={(e) => setNewFieldValue(e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4c2f8e]"
                      />
                    )}

                    <button
                      type="button"
                      onClick={addField}
                      disabled={isSaving}
                      className="bg-[#4c2f8e] text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-[#3d2575] transition disabled:opacity-60"
                    >
                      Ajouter
                    </button>
                  </div>

                  <div className="mt-3 text-xs text-gray-500">
                    Pour ajouter manuellement le champ des victoires, utilise le
                    nom exact :{' '}
                    <code className="bg-gray-100 px-2 py-1 rounded">
                      winTournement
                    </code>
                    . Mais il est déjà affiché automatiquement plus haut.
                  </div>
                </div>

                <div className="mt-6 flex flex-col md:flex-row justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(selectedJoueur.id)}
                    className="bg-white border border-purple-200 text-[#4c2f8e] px-5 py-3 rounded-xl font-bold hover:border-[#4c2f8e] transition"
                  >
                    {copiedId === selectedJoueur.id
                      ? 'ID copié ✅'
                      : 'Copier ID Firestore'}
                  </button>

                  <button
                    type="button"
                    onClick={saveJoueur}
                    disabled={isSaving}
                    className="bg-[#4c2f8e] text-white px-8 py-3 rounded-xl font-black hover:bg-[#3d2575] transition disabled:opacity-60"
                  >
                    {isSaving
                      ? 'Sauvegarde...'
                      : 'Enregistrer les modifications'}
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-4">
                  Cette page modifie uniquement le document sélectionné dans la
                  collection joueurs. Elle ne supprime pas le joueur complet.
                  Le champ winTournement est conservé avec cette orthographe
                  exacte.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default AdminJoueurs;